import React from 'react';
import Icon from '../../../components/AppIcon';

const FlightTrackingModule = ({ flightInfo, currentLanguage }) => {
  const translations = {
    en: {
      title: 'Flight Tracking',
      noFlights: 'No flight issues detected',
      flightNumber: 'Flight',
      route: 'Route',
      status: 'Status',
      delayed: 'Delayed',
      cancelled: 'Cancelled',
      inFlight: 'In Flight',
      scheduled: 'Scheduled'
    },
    ko: {
      title: '항공편 추적',
      noFlights: '항공편 문제가 감지되지 않았습니다',
      flightNumber: '항공편',
      route: '경로',
      status: '상태',
      delayed: '지연',
      cancelled: '취소',
      inFlight: '비행 중',
      scheduled: '예정'
    }
  };

  const t = translations?.[currentLanguage];

  const getStatusConfig = (status) => {
    const configs = {
      delayed: {
        label: t?.delayed,
        icon: 'AlertTriangle',
        color: 'text-warning',
        bgColor: 'bg-warning/10'
      },
      cancelled: {
        label: t?.cancelled,
        icon: 'XCircle',
        color: 'text-error',
        bgColor: 'bg-error/10'
      },
      in_flight: {
        label: t?.inFlight,
        icon: 'Plane',
        color: 'text-primary',
        bgColor: 'bg-primary/10'
      },
      scheduled: {
        label: t?.scheduled,
        icon: 'Clock',
        color: 'text-muted-foreground',
        bgColor: 'bg-muted/10'
      }
    };
    return configs?.[status] || configs?.scheduled;
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Plane" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">{t?.title}</h2>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {flightInfo?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t?.noFlights}</p>
          </div>
        ) : (
          flightInfo?.map((flight) => {
            const statusConfig = getStatusConfig(flight?.flightStatus);
            return (
              <div
                key={flight?.id}
                className="border border-border rounded-lg p-3 hover:bg-accent/5 transition-base"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 ${statusConfig?.bgColor} rounded-full flex items-center justify-center`}>
                      <Icon name={statusConfig?.icon} size={16} className={statusConfig?.color} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {flight?.airline} {flight?.flightNumber}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {flight?.trip?.fromCountry} → {flight?.trip?.toCountry}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusConfig?.color} ${statusConfig?.bgColor}`}>
                    {statusConfig?.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">{currentLanguage === 'en' ? 'Departure' : '출발'}</p>
                    <p className="font-medium text-foreground">
                      {new Date(flight?.departureTime)?.toLocaleString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{currentLanguage === 'en' ? 'Arrival' : '도착'}</p>
                    <p className="font-medium text-foreground">
                      {new Date(flight?.arrivalTime)?.toLocaleString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {flight?.flightStatus === 'delayed' && (
                  <div className="mt-2 p-2 bg-warning/10 rounded text-xs text-warning">
                    <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                    {currentLanguage === 'en' ? 'Automatic rematch may be triggered' : '자동 재매칭이 트리거될 수 있습니다'}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FlightTrackingModule;